import config from '../config';
import type { AnyRecord } from '../utils/types';
import { httpClient } from './http';
import { TOKEN_NAME } from './token';
import { uuid } from './uuid';

const STORAGE_KEY = TOKEN_NAME;

const DEFAULT_OPTIONS = {
  autoRefreshToken: true,
  headers: {},
  persistSession: true,
  url: config.api.host,
};

interface IAuthOptions {
  autoRefreshToken: boolean;
  headers: AnyRecord;
  persistSession: boolean;
  url: string;
}

interface IAuthUser {
  id: string;
  name: string;
}

interface IAuthUserSession {
  id: string;
  name: string;
  session: {
    access_token: string;
    refresh_token: string;
    expired_at: number;
  };
}

interface ICallbackSubscription {
  id: string;
  callback: (event: string, data: any) => any;
  unsubscribe: () => any;
}

class AuthClient {
  private options: IAuthOptions;

  private refreshTokenTimer: number | undefined;

  private currentUser: IAuthUser | undefined;

  private currentSession: IAuthUserSession | undefined;

  private stateChangeEmitters = new Map<string, ICallbackSubscription>();

  constructor(options?: IAuthOptions) {
    this.options = {
      ...DEFAULT_OPTIONS,
      ...(options && options),
    };

    this.recoverSession();
    this.recoverAndRefresh();
  }

  getUser(): IAuthUser | undefined {
    return this.currentUser;
  }

  getSession(): IAuthUserSession | undefined {
    return this.currentSession;
  }

  private recoverSession() {
    try {
      const json = localStorage?.getItem(STORAGE_KEY);
      if (!json || typeof json !== 'string') {
        return null;
      }

      const data = JSON.parse(json);
      const { currentSession, expiresAt } = data;
      const timeNow = Date.now();

      if (expiresAt >= timeNow && currentSession?.session) {
        console.log('Recovering session');
        this.saveSession(currentSession);
        this.notifyAllSubscribers('SIGNED_IN');
      }
    } catch (error) {
      console.log('error', error);
    }
  }

  private async recoverAndRefresh() {
    try {
      const json = localStorage.getItem(STORAGE_KEY);
      if (!json) {
        return null;
      }

      const data = JSON.parse(json);
      const { currentSession, expiresAt } = data;
      const timeNow = Date.now();

      if (expiresAt < timeNow) {
        if (
          this.options.autoRefreshToken &&
          currentSession?.session?.refresh_token
        ) {
          console.log('Recovering session and refresh');
          try {
            this.currentSession = currentSession;
            await this.callRefreshToken();
          } catch (error: any) {
            console.log(error?.message);
            this.removeSession();
          }
        } else {
          this.removeSession();
        }
      } else if (!currentSession || !currentSession.session) {
        console.log('Current session is missing data.');
        this.removeSession();
      } else {
        // Should be handled in recoverSession method already
        // But we still need the code here to accommodate for AsyncStorage e.g. in React native
        this.saveSession(currentSession);
        this.notifyAllSubscribers('SIGNED_IN');
      }
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  async requestCode(email: string): Promise<void> {
    if (!email) throw new Error('You must provide email');

    await httpClient.post<{ data: IAuthUserSession }>({
      apiPath: '/v1/auth/request-code',
      data: {
        email,
      },
      headers: {},
    });
  }

  async signOut(): Promise<void> {
    this.removeSession();
    Promise.resolve();
  }

  async signIn(email: string, code: string): Promise<void> {
    this.removeSession();

    if (!email || !code)
      throw new Error('You must provide email and login code');

    const result = await httpClient.post<{ data: IAuthUserSession }>({
      apiPath: '/v1/auth/login',
      data: {
        email,
        code,
      },
      headers: {},
    });

    if (result) {
      this.saveSession(result.data);
      this.notifyAllSubscribers('SIGNED_IN');
    }
  }

  onAuthStateChange(callback: any): void {
    const id = uuid();
    const subscription = {
      callback,
      id,
      unsubscribe: () => {
        this.stateChangeEmitters.delete(id);
      },
    };
    this.stateChangeEmitters.set(id, subscription);
  }

  async callRefreshToken(): Promise<void> {
    if (!this.currentSession?.session?.refresh_token) {
      throw new Error('No current session.');
    }
    const result = await httpClient.post<{ data: IAuthUserSession }>({
      apiPath: '/v1/auth/refresh-token',
      data: {
        token: this.currentSession?.session?.refresh_token,
      },
      headers: {},
    });
    if (result) {
      this.saveSession(result.data);
      this.notifyAllSubscribers('REFRESHED_TOKEN');
    }
  }

  private notifyAllSubscribers(event: string): void {
    for (const [, x] of this.stateChangeEmitters)
      x.callback(event, this.currentSession);
  }

  private saveSession(session: IAuthUserSession): void {
    this.currentSession = session;
    this.currentUser = {
      id: session.id,
      name: session.name,
    };

    const expiresAt = session.session?.expired_at;
    if (expiresAt) {
      const refreshDurationBeforeExpires = expiresAt - Date.now() - 5000;
      this.startAutoRefreshToken(refreshDurationBeforeExpires);
    }

    this.persistSession(this.currentSession);
  }

  private persistSession(currentSession: IAuthUserSession): void {
    const data = {
      currentSession,
      expiresAt: currentSession.session?.expired_at,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  private removeSession(): void {
    console.log('Removing current session.');
    this.currentSession = undefined;
    this.currentUser = undefined;

    if (this.refreshTokenTimer) clearTimeout(this.refreshTokenTimer);

    localStorage.removeItem(STORAGE_KEY);
  }

  private startAutoRefreshToken(value: number): void {
    if (this.refreshTokenTimer) clearTimeout(this.refreshTokenTimer);
    if (value <= 0 || !this.options.autoRefreshToken) return;

    this.refreshTokenTimer = setTimeout(
      () => this.callRefreshToken(),
      value,
    ) as unknown as number;
  }
}

export default new AuthClient();
